import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { captureDocument as scanDocumentFromCamera } from '../services/camera';
import FormattedMessage from '../components/FormattedMessage';
import MessageAttachment from '../components/MessageAttachment';
import { formatDateUtc } from '../utils/dateFormat';
import { compressAttachment } from '../services/compress';
import ConfirmDialog from './ConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCustomerDetail = () => {
  const { customerId } = useParams();
  const [customer, setCustomer] = useState(null);
  const [investment, setInvestment] = useState(null);
  const [accruals, setAccruals] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showInvestmentModal, setShowInvestmentModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [showBankModal, setShowBankModal] = useState(false);
  const [showCreatePayout, setShowCreatePayout] = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [advisors, setAdvisors] = useState([]);
  const [savingAdvisor, setSavingAdvisor] = useState(false);

  useEffect(() => {
    loadCustomerData();
    loadAdvisors();
  }, [customerId]);

  const loadAdvisors = async () => {
    try {
      const res = await axios.get(`${API}/admin/advisors`);
      setAdvisors((res.data || []).filter((a) => a.active));
    } catch (e) {
      // silent — feature optional
    }
  };

  const assignAdvisor = async (advisorId) => {
    setSavingAdvisor(true);
    try {
      await axios.patch(`${API}/admin/customers/${customerId}/advisor`, {
        advisor_id: advisorId ? Number(advisorId) : null,
      });
      toast.success('Berater zugewiesen');
      loadCustomerData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Zuweisung fehlgeschlagen');
    } finally {
      setSavingAdvisor(false);
    }
  };

  const loadCustomerData = async () => {
    try {
      const customerRes = await axios.get(`${API}/admin/customers/${customerId}`);
      setCustomer(customerRes.data);

      try {
        const investmentRes = await axios.get(`${API}/admin/customers/${customerId}/investment`);
        if (investmentRes.data) {
          setInvestment(investmentRes.data);
          loadInvestmentDetails(investmentRes.data.id);
        }
      } catch (error) {
        console.log('No investment found');
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Kundendaten');
    } finally {
      setLoading(false);
    }
  };

  const loadInvestmentDetails = async (investmentId) => {
    try {
      const [accrualsRes, payoutsRes] = await Promise.all([
        axios.get(`${API}/admin/investments/${investmentId}/accruals`),
        axios.get(`${API}/admin/investments/${investmentId}/payouts`),
      ]);
      setAccruals(accrualsRes.data);
      setPayouts(payoutsRes.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Investment-Details');
    }
  };

  const loadAuditLogs = async () => {
    setAuditLoading(true);
    try {
      const response = await axios.get(`${API}/admin/customers/${customerId}/audit-logs`);
      setAuditLogs(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Audit-Logs');
    } finally {
      setAuditLoading(false);
    }
  };

  const updateStatus = async (newStatus, note) => {
    try {
      await axios.post(`${API}/admin/investments/${investment.id}/status`, {
        status: newStatus,
        note: note || ''
      });
      toast.success('Status aktualisiert');
      loadCustomerData();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren des Status');
    }
  };

  const recalculate = async () => {
    try {
      await axios.post(`${API}/admin/investments/${investment.id}/recalculate`);
      toast.success('Neuberechnung durchgeführt');
      loadInvestmentDetails(investment.id);
    } catch (error) {
      toast.error('Fehler bei der Neuberechnung');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const translatePayoutMode = (mode) => {
    const translations = {
      'MONTHLY': 'Monatlich',
      'YEARLY': 'Jährlich'
    };
    return translations[mode] || mode;
  };

  const translatePayoutTiming = (timing) => {
    const translations = {
      'MONTH_START': 'Monatsanfang',
      'MONTH_END': 'Monatsende',
      'ANNIVERSARY_MONTHLY': 'Monatl. Jahrestag',
      'ANNIVERSARY_YEARLY': 'Jährl. Jahrestag',
      'YEAR_END': 'Jahresende'
    };
    return translations[timing] || timing;
  };

  const translateStatus = (status) => {
    const translations = {
      'CONTRACT_SIGNED': 'Vertrag unterzeichnet',
      'FUNDS_INVESTED': 'Geld eingelegt',
      'PAYOUTS_ACTIVE': 'Auszahlungen aktiv',
      'PAUSED': 'Pausiert',
      'TERMINATED': 'Beendet'
    };
    return translations[status] || status.replace('_', ' ');
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading font-bold">{customer?.name || customer?.email}</h2>
          <p className="text-muted-foreground text-sm mt-1">{customer?.email}</p>
        </div>
        <div className="flex items-center gap-2" data-testid="advisor-assignment">
          <label className="text-xs uppercase tracking-wider text-muted-foreground">Berater:</label>
          <select
            value={customer?.advisor_id || ''}
            onChange={(e) => assignAdvisor(e.target.value || null)}
            disabled={savingAdvisor}
            className="px-3 py-1.5 border rounded text-sm bg-white"
            data-testid="advisor-select"
          >
            <option value="">— Kein Berater —</option>
            {advisors.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="mb-6 border-b">
        <div className="flex space-x-8 overflow-x-auto">
          {['overview', 'bank', 'accruals', 'payouts', 'documents', 'messages', 'audit'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-3 px-1 text-sm font-medium transition whitespace-nowrap ${
                activeTab === tab
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-muted-foreground hover:text-primary'
              }`}
              data-testid={`tab-${tab}`}
            >
              {tab === 'overview' ? 'Übersicht' : 
               tab === 'bank' ? 'Bankverbindung' :
               tab === 'accruals' ? 'Zinsen' : 
               tab === 'payouts' ? 'Auszahlungen' :
               tab === 'documents' ? 'Dokumente' :
               tab === 'messages' ? 'Nachrichten' : 'Audit Log'}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'overview' && (
        <div className="space-y-6">
          {!investment ? (
            <div className="card text-center py-12">
              <p className="text-muted-foreground mb-4">Noch kein Investment angelegt</p>
              <button onClick={() => setShowInvestmentModal(true)} className="btn-primary">
                Investment erstellen
              </button>
            </div>
          ) : (
            <>
              <div className="card" data-testid="investment-overview">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="font-heading font-semibold text-lg">Investment</h3>
                    {investment.nickname && (
                      <p className="text-sm text-accent mt-1 italic" data-testid="investment-nickname-admin">
                        Kunden-Bezeichnung: „{investment.nickname}"
                      </p>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button 
                      onClick={() => setShowEditModal(true)} 
                      className="text-sm px-3 py-1 bg-secondary hover:bg-gray-200 rounded transition"
                      data-testid="edit-investment-button"
                    >
                      Bearbeiten
                    </button>
                    <button
                      onClick={() => setShowExtendModal(true)}
                      className="text-sm px-3 py-1 bg-accent/10 text-accent hover:bg-accent/20 rounded transition font-medium"
                      data-testid="extend-investment-button"
                    >
                      Verlängern
                    </button>
                    <button
                      onClick={() => {
                        setConfirmState({
                          title: 'Vertrag neu berechnen?',
                          message: 'Bereits bestätigte (ausgezahlte) Auszahlungen bleiben unverändert. Nicht-bestätigte (fällige) Auszahlungen und alle Zinsperioden werden auf Basis des Vertragsbeginns neu generiert.',
                          confirmLabel: 'Neu berechnen',
                          danger: false,
                          onConfirm: async () => {
                            try {
                              await axios.post(`${API}/admin/investments/${investment.id}/recalculate`);
                              toast.success('Vertrag neu berechnet');
                              loadCustomerData();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Neuberechnung fehlgeschlagen');
                            }
                          },
                        });
                      }}
                      className="text-sm px-3 py-1 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded transition font-medium"
                      data-testid="recalculate-investment-button"
                      title="Accruals + Payouts neu berechnen"
                    >
                      Neu berechnen
                    </button>
                    <span className="text-xs px-3 py-1 rounded-full bg-accent text-white font-medium">
                      {translateStatus(investment.status)}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Produkt</p>
                    <p className="text-lg font-semibold">{investment.product_code}</p>
                    <p className="text-sm text-accent">{(investment.apr * 100).toFixed(1)}% p.a.</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Einlage</p>
                    <p className="text-lg font-semibold" data-testid="principal-amount">{formatCurrency(investment.principal_eur)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Laufzeit</p>
                    <p className="text-sm font-medium">
                      {investment.duration_months ? `${investment.duration_months} Monate` : 'Unbefristet'}
                    </p>
                    {investment.interest_start_date && (
                      <p className="text-xs text-muted-foreground">
                        {formatDateUtc(investment.interest_start_date)}
                        {investment.interest_end_date && (
                          <> - {formatDateUtc(investment.interest_end_date)}</>
                        )}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Auszahlungsmodus</p>
                    <p className="text-sm font-medium">{translatePayoutMode(investment.payout_mode)}</p>
                    <p className="text-xs text-muted-foreground">{translatePayoutTiming(investment.payout_timing)}</p>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t flex space-x-3">
                  <StatusUpdateModal investment={investment} onUpdate={updateStatus} />
                  <button onClick={recalculate} className="btn-secondary text-sm" data-testid="recalculate-button">
                    Neuberechnen
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="card">
                  <h4 className="font-semibold mb-3">aufgelaufene Zinsen</h4>
                  <p className="text-2xl font-bold text-success" data-testid="total-accrued">
                    {formatCurrency(accruals.reduce((sum, a) => sum + a.interest_amount_eur, 0))}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">{accruals.length} Perioden</p>
                </div>

                <div className="card">
                  <h4 className="font-semibold mb-3">ausgezahlte Zinsen</h4>
                  <p className="text-2xl font-bold text-info" data-testid="total-payouts">
                    {formatCurrency(payouts.filter(p => p.status === 'confirmed').reduce((sum, p) => sum + p.amount_eur, 0))}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {payouts.filter(p => p.status === 'due').length} fällig · {payouts.filter(p => p.status === 'confirmed').length} bestätigt
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'bank' && (
        <div className="space-y-6">
          <div className="card" style={{ 
            background: 'linear-gradient(145deg, #0A0A0A 0%, #1a1a1a 100%)',
            border: 'none'
          }}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(197, 160, 89, 0.2)' }}>
                  <svg className="w-6 h-6" style={{ color: '#C5A059' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                  </svg>
                </div>
                <div>
                  <p className="text-white font-semibold">Bankverbindung</p>
                  <p className="text-gray-400 text-xs">{customer?.name}</p>
                </div>
              </div>
              <button
                onClick={() => setShowBankModal(true)}
                className="px-4 py-2 rounded-lg text-sm font-medium transition"
                style={{ backgroundColor: 'rgba(197, 160, 89, 0.2)', color: '#C5A059' }}
                data-testid="edit-bank-button"
              >
                Bearbeiten
              </button>
            </div>

            {customer?.bank_iban ? (
              <div className="space-y-4">
                <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                  <p className="text-gray-400 text-xs mb-1">Kontoinhaber</p>
                  <p className="text-white font-medium text-lg">{customer.bank_account_holder || '-'}</p>
                </div>
                
                <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                  <p className="text-gray-400 text-xs mb-1">IBAN</p>
                  <p className="text-white font-mono text-lg tracking-wider">
                    {customer.bank_iban.replace(/(.{4})/g, '$1 ').trim()}
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                    <p className="text-gray-400 text-xs mb-1">BIC</p>
                    <p className="text-white font-mono">{customer.bank_bic || '-'}</p>
                  </div>
                  <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                    <p className="text-gray-400 text-xs mb-1">Bank</p>
                    <p className="text-white">{customer.bank_name || '-'}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <p className="text-gray-400 mb-4">Keine Bankverbindung hinterlegt</p>
                <button
                  onClick={() => setShowBankModal(true)}
                  className="btn-primary"
                  data-testid="add-bank-button"
                >
                  Bankverbindung hinzufügen
                </button>
              </div>
            )}
          </div>

          <div className="card">
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(197, 160, 89, 0.1)' }}>
                <svg className="w-5 h-5" style={{ color: '#C5A059' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h4 className="font-semibold text-primary mb-1">Hinweis</h4>
                <p className="text-sm text-muted-foreground">
                  Änderungen an der Bankverbindung werden im Audit-Log protokolliert. 
                  Alle Auszahlungen erfolgen auf das hier hinterlegte Konto.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'accruals' && (
        <div className="card" data-testid="accruals-tab">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold">Zinsen ({accruals.length})</h3>
            <p className="text-xs text-muted-foreground">Beträge sind editierbar — Änderungen werden im Audit-Log protokolliert.</p>
          </div>
          <div className="space-y-2">
            {accruals.map((accrual) => (
              <AccrualRow key={accrual.id} accrual={accrual} onUpdated={loadCustomerData} onConfirm={setConfirmState} />
            ))}
            {accruals.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-6">Noch keine Zinsperioden vorhanden.</p>
            )}
          </div>
        </div>
      )}

      {activeTab === 'payouts' && (
        <div className="card" data-testid="payouts-tab">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold">Auszahlungen ({payouts.length})</h3>
            <button
              onClick={() => setShowCreatePayout(true)}
              className="text-sm px-3 py-1.5 bg-accent/10 text-accent hover:bg-accent/20 rounded transition font-medium"
              data-testid="create-payout-button"
            >
              + Manuelle Auszahlung
            </button>
          </div>
          <div className="space-y-3">
            {payouts.map((payout) => (
              <PayoutRow
                key={payout.id}
                payout={payout}
                onUpdated={loadCustomerData}
                onConfirm={setConfirmState}
              />
            ))}
            {payouts.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-6">Noch keine Auszahlungen vorhanden.</p>
            )}
          </div>
        </div>
      )}

      {activeTab === 'documents' && (
        <AdminDocumentsTab customerId={customerId} />
      )}

      {activeTab === 'messages' && (
        <AdminMessagesTab customerId={customerId} customerName={customer?.name || customer?.email} />
      )}

      {activeTab === 'audit' && (
        <AuditLogTab 
          customerId={customerId} 
          auditLogs={auditLogs} 
          loading={auditLoading} 
          onLoad={loadAuditLogs}
        />
      )}

      {showInvestmentModal && (
        <CreateInvestmentModal
          customerId={customerId}
          onClose={() => setShowInvestmentModal(false)}
          onSuccess={() => {
            setShowInvestmentModal(false);
            loadCustomerData();
          }}
        />
      )}

      {showEditModal && investment && (
        <EditInvestmentModal
          investment={investment}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            loadCustomerData();
          }}
        />
      )}

      {showExtendModal && investment && (
        <ExtendInvestmentModal
          investment={investment}
          onClose={() => setShowExtendModal(false)}
          onSuccess={() => {
            setShowExtendModal(false);
            loadCustomerData();
          }}
        />
      )}

      {showBankModal && (
        <EditBankModal
          customer={customer}
          onClose={() => setShowBankModal(false)}
          onSuccess={() => {
            setShowBankModal(false);
            loadCustomerData();
          }}
        />
      )}

      {showCreatePayout && investment && (
        <CreatePayoutModal
          investmentId={investment.id}
          onClose={() => setShowCreatePayout(false)}
          onCreated={loadCustomerData}
        />
      )}

      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
    </div>
  );
};

const formatCurrencyEUR = (amount) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount || 0);

const AccrualRow = ({ accrual, onUpdated, onConfirm }) => {
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState(accrual.interest_amount_eur);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const parsed = parseFloat(String(amount).replace(',', '.'));
    if (isNaN(parsed) || parsed < 0) {
      toast.error('Bitte einen gültigen Betrag eingeben');
      return;
    }
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/accruals/${accrual.id}`, { interest_amount_eur: parsed });
      toast.success('Zinsperiode aktualisiert');
      setEditing(false);
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const remove = () => {
    onConfirm?.({
      title: 'Zinsperiode löschen?',
      message: `${formatDateUtc(accrual.period_start)} – ${formatDateUtc(accrual.period_end)} (+${formatCurrencyEUR(accrual.interest_amount_eur)}) wird unwiderruflich entfernt.`,
      confirmLabel: 'Löschen',
      danger: true,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/admin/accruals/${accrual.id}`);
          toast.success('Zinsperiode gelöscht');
          onUpdated?.();
        } catch (e) {
          toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen');
        }
      },
    });
  };

  return (
    <div className="flex justify-between items-center gap-3 p-3 bg-background-subtle rounded" data-testid={`accrual-${accrual.id}`}>
      <span className="text-sm tabular-nums whitespace-nowrap">
        {formatDateUtc(accrual.period_start)} – {formatDateUtc(accrual.period_end)}
      </span>
      {editing ? (
        <div className="flex items-center gap-2">
          <input
            type="number"
            step="0.01"
            min="0"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            disabled={saving}
            className="w-28 px-2 py-1 text-sm border rounded text-right tabular-nums"
            data-testid={`accrual-input-${accrual.id}`}
            autoFocus
          />
          <button onClick={save} disabled={saving} className="text-xs px-2 py-1 bg-[#0A0A0A] text-white rounded hover:bg-[#1F1F1F]" data-testid={`accrual-save-${accrual.id}`}>
            {saving ? '…' : 'OK'}
          </button>
          <button onClick={() => { setEditing(false); setAmount(accrual.interest_amount_eur); }} disabled={saving} className="text-xs px-2 py-1 bg-secondary rounded hover:bg-gray-200">Abbrechen</button>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <span className="font-semibold text-success tabular-nums">+{formatCurrencyEUR(accrual.interest_amount_eur)}</span>
          <button onClick={() => setEditing(true)} className="text-xs text-accent hover:underline" data-testid={`accrual-edit-${accrual.id}`}>Bearbeiten</button>
          <button onClick={remove} className="text-xs text-red-600 hover:underline" data-testid={`accrual-delete-${accrual.id}`}>Löschen</button>
        </div>
      )}
    </div>
  );
};

const CreatePayoutModal = ({ investmentId, defaultAmount = '', onClose, onCreated }) => {
  const today = new Date().toISOString().split('T')[0];
  const [amount, setAmount] = useState(defaultAmount);
  const [dueDate, setDueDate] = useState(today);
  const [status, setStatus] = useState('due');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    const parsed = parseFloat(String(amount).replace(',', '.'));
    if (isNaN(parsed) || parsed <= 0) {
      toast.error('Bitte gültigen Betrag eingeben');
      return;
    }
    if (!dueDate) {
      toast.error('Fälligkeitsdatum erforderlich');
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('amount_eur', String(parsed));
      fd.append('due_date', new Date(dueDate + 'T00:00:00Z').toISOString());
      fd.append('status', status);
      if (reference) fd.append('reference', reference);
      if (note) fd.append('note', note);
      if (status === 'confirmed' && receipt) fd.append('receipt', receipt);
      await axios.post(`${API}/admin/investments/${investmentId}/payouts`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(status === 'confirmed' && receipt ? 'Auszahlung + Beleg gespeichert' : 'Auszahlung erstellt');
      onCreated?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Anlage fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!['application/pdf', 'image/jpeg', 'image/png'].includes(f.type)) {
      toast.error('Nur PDF, JPG oder PNG erlaubt');
      e.target.value = '';
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error('Datei zu groß (max. 10 MB)');
      e.target.value = '';
      return;
    }
    setReceipt(f);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 vp-anim-fade" onClick={() => !saving && onClose?.()}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto vp-anim-scale" data-testid="create-payout-modal">
        <h3 className="text-lg font-heading font-bold">Manuelle Auszahlung anlegen</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1">Betrag (EUR) *</label>
            <input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} required className="w-full px-3 py-2 border rounded text-right tabular-nums" data-testid="create-payout-amount" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Fälligkeitsdatum *</label>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} required className="w-full px-3 py-2 border rounded" data-testid="create-payout-due-date" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full px-3 py-2 border rounded" data-testid="create-payout-status">
            <option value="due">Fällig (offen)</option>
            <option value="confirmed">Bestätigt (sofort als ausgezahlt)</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Referenz (optional)</label>
          <input type="text" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="z.B. Überweisungsnummer" className="w-full px-3 py-2 border rounded" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Notiz (optional)</label>
          <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="w-full px-3 py-2 border rounded resize-none" />
        </div>

        {status === 'confirmed' && (
          <div className="border-t pt-4">
            <label className="block text-sm font-medium mb-2">Beleg (optional)</label>
            <input
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              onChange={onFileChange}
              className="w-full text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-accent/10 file:text-accent hover:file:bg-accent/20 file:cursor-pointer"
              data-testid="create-payout-receipt"
            />
            {receipt && (
              <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                <span>📎 {receipt.name}</span>
                <button type="button" onClick={() => setReceipt(null)} className="text-red-600 hover:underline">Entfernen</button>
              </div>
            )}
            <p className="text-[11px] text-muted-foreground mt-1">PDF, JPG oder PNG · max. 10 MB · Wird im Kunden-Portal zum Download angeboten</p>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} disabled={saving} className="btn-secondary">Abbrechen</button>
          <button type="submit" disabled={saving} className="btn-primary" data-testid="create-payout-save">
            {saving ? 'Speichert …' : 'Auszahlung anlegen'}
          </button>
        </div>
      </form>
    </div>
  );
};


const PayoutRow = ({ payout, onUpdated, onConfirm }) => {
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState(payout.amount_eur);
  const [note, setNote] = useState(payout.note || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const parsed = parseFloat(String(amount).replace(',', '.'));
    if (isNaN(parsed) || parsed < 0) {
      toast.error('Bitte einen gültigen Betrag eingeben');
      return;
    }
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/payouts/${payout.id}`, {
        amount_eur: parsed,
        note: note || null,
      });
      toast.success('Auszahlung aktualisiert');
      setEditing(false);
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const remove = () => {
    onConfirm?.({
      title: 'Auszahlung löschen?',
      message: `${formatCurrencyEUR(payout.amount_eur)} vom ${formatDateUtc(payout.due_date)} wird unwiderruflich entfernt — inklusive eventueller Belege.${payout.status === 'confirmed' ? '\n\nAchtung: Diese Auszahlung wurde bereits bestätigt.' : ''}`,
      confirmLabel: 'Löschen',
      danger: true,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/admin/payouts/${payout.id}`);
          toast.success('Auszahlung gelöscht');
          onUpdated?.();
        } catch (e) {
          toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen');
        }
      },
    });
  };

  if (editing) {
    return (
      <div className="p-4 border-2 border-primary rounded bg-primary/5" data-testid={`payout-edit-${payout.id}`}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-xs font-medium mb-1">Betrag (EUR)</label>
            <input
              type="number" step="0.01" className="input-field"
              value={amount} onChange={(e) => setAmount(e.target.value)}
              data-testid={`payout-amount-input-${payout.id}`}
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Notiz (optional)</label>
            <input
              type="text" className="input-field"
              value={note} onChange={(e) => setNote(e.target.value)}
              data-testid={`payout-note-input-${payout.id}`}
              placeholder="z.B. Korrektur nach Neuberechnung"
            />
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <button
            onClick={() => { setEditing(false); setAmount(payout.amount_eur); setNote(payout.note || ''); }}
            className="btn-secondary text-xs" disabled={saving}
          >
            Abbrechen
          </button>
          <button
            onClick={save}
            className="btn-primary text-xs" disabled={saving}
            data-testid={`payout-save-${payout.id}`}
          >
            {saving ? 'Speichert...' : 'Speichern'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 border rounded" data-testid={`payout-${payout.id}`}>
      <div className="flex justify-between items-start gap-3 flex-wrap">
        <div>
          <p className="font-semibold">{formatCurrencyEUR(payout.amount_eur)}</p>
          <p className="text-sm text-muted-foreground">
            Fällig: {formatDateUtc(payout.due_date)}
          </p>
          {payout.confirmed_at && (
            <p className="text-xs text-success mt-1">
              Bestätigt: {format(new Date(payout.confirmed_at), 'dd.MM.yyyy HH:mm', { locale: de })}
            </p>
          )}
          {payout.note && (
            <p className="text-xs text-muted-foreground mt-1">Notiz: {payout.note}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-1 rounded ${
            payout.status === 'confirmed' ? 'bg-success text-white' :
            payout.status === 'due' ? 'bg-warning text-white' :
            'bg-gray-300 text-gray-700'
          }`}>
            {payout.status === 'confirmed' ? 'Bestätigt' : payout.status === 'due' ? 'Fällig' : payout.status}
          </span>
          <button
            onClick={() => setEditing(true)}
            className="btn-secondary text-xs"
            data-testid={`payout-edit-button-${payout.id}`}
            title="Betrag/Notiz anpassen"
          >
            Bearbeiten
          </button>
          <button
            onClick={remove}
            className="text-xs px-3 py-1.5 bg-red-50 text-red-700 hover:bg-red-100 rounded transition font-medium"
            data-testid={`payout-delete-button-${payout.id}`}
            title="Auszahlung löschen"
          >
            Löschen
          </button>
        </div>
      </div>
    </div>
  );
};

const StatusUpdateModal = ({ investment, onUpdate }) => {
  const [showModal, setShowModal] = useState(false);
  const [status, setStatus] = useState(investment.status);
  const [note, setNote] = useState('');

  const statuses = [
    'CONTRACT_SIGNED',
    'PAYMENT_CONFIRMED',
    'FUNDS_INVESTED',
    'PAYOUTS_ACTIVE'
  ];

  const handleSubmit = () => {
    onUpdate(status, note);
    setShowModal(false);
  };

  return (
    <>
      <button onClick={() => setShowModal(true)} className="btn-primary text-sm" data-testid="update-status-button">
        Status ändern
      </button>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-heading font-bold mb-4">Status ändern</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Neuer Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="input-field"
                  data-testid="status-select"
                >
                  {statuses.map((s) => (
                    <option key={s} value={s}>{s.replace('_', ' ')}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Notiz (optional)</label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="input-field"
                  rows={3}
                  data-testid="status-note-input"
                />
              </div>

              <div className="flex space-x-3">
                <button onClick={() => setShowModal(false)} className="btn-secondary flex-1">Abbrechen</button>
                <button onClick={handleSubmit} className="btn-primary flex-1" data-testid="status-submit-button">Speichern</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const CreateInvestmentModal = ({ customerId, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    product_code: 'BASIS',
    principal_eur: 0,
    payout_mode: 'MONTHLY',
    payout_timing: 'MONTH_START',
    interest_start_date: new Date().toISOString().split('T')[0],
    duration_months: 36,
    notes: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/admin/customers/${customerId}/investment`, formData);
      toast.success('Investment erstellt');
      onSuccess();
    } catch (error) {
      toast.error('Fehler beim Erstellen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-heading font-bold mb-4">Investment erstellen</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Produkt</label>
            <select
              value={formData.product_code}
              onChange={(e) => setFormData({ ...formData, product_code: e.target.value })}
              className="input-field"
              data-testid="investment-product-select"
            >
              <option value="BASIS">BASIS (5.0% p.a.)</option>
              <option value="ERHÖHT">ERHÖHT (6.5% p.a.)</option>
              <option value="PREMIUM">PREMIUM (9.7% p.a.)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Einlage (EUR)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              className="input-field"
              value={formData.principal_eur}
              onChange={(e) => setFormData({ ...formData, principal_eur: parseFloat(e.target.value) })}
              required
              data-testid="investment-principal-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Zinsbeginn (Startdatum)</label>
            <input
              type="date"
              className="input-field"
              value={formData.interest_start_date}
              onChange={(e) => setFormData({ ...formData, interest_start_date: e.target.value })}
              required
              data-testid="investment-start-date-input"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Für rückwirkende Einträge kann ein Datum in der Vergangenheit gewählt werden.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Laufzeit (Monate)</label>
            <div className="flex gap-2 items-center">
              <input
                type="number"
                min={1}
                max={240}
                value={formData.duration_months}
                onChange={(e) => setFormData({ ...formData, duration_months: parseInt(e.target.value) || 0 })}
                className="input-field flex-1"
                data-testid="investment-duration-input"
              />
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                {formData.duration_months > 0 && (
                  <>≈ {(formData.duration_months / 12).toFixed(formData.duration_months % 12 === 0 ? 0 : 1)} Jahre</>
                )}
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {[12, 18, 24, 36, 48, 60].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setFormData({ ...formData, duration_months: m })}
                  className={`text-xs px-2.5 py-1 rounded-full border transition ${
                    formData.duration_months === m
                      ? 'bg-accent text-white border-accent'
                      : 'border-gray-300 hover:border-accent hover:bg-accent/5'
                  }`}
                  data-testid={`duration-quick-${m}`}
                >
                  {m}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Beliebige Laufzeit zwischen 1 und 240 Monaten möglich. Nach Ablauf werden keine Zinsen mehr berechnet.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Auszahlungsmodus</label>
            <select
              value={formData.payout_mode}
              onChange={(e) => setFormData({ ...formData, payout_mode: e.target.value })}
              className="input-field"
              data-testid="investment-payout-mode-select"
            >
              <option value="MONTHLY">Monatlich</option>
              <option value="YEARLY">Jährlich</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Auszahlungszeitpunkt</label>
            <select
              value={formData.payout_timing}
              onChange={(e) => setFormData({ ...formData, payout_timing: e.target.value })}
              className="input-field"
              data-testid="investment-payout-timing-select"
            >
              <option value="MONTH_START">Monatsanfang</option>
              <option value="MONTH_END">Monatsende</option>
              <option value="ANNIVERSARY_MONTHLY">Monatlicher Jahrestag</option>
              <option value="ANNIVERSARY_YEARLY">Jährlicher Jahrestag</option>
              <option value="YEAR_END">Jahresende</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Notizen</label>
            <textarea
              className="input-field"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              placeholder="Interne Notizen zum Investment..."
              data-testid="investment-notes-input"
            />
          </div>

          <div className="flex space-x-3 mt-6">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Abbrechen</button>
            <button type="submit" className="btn-primary flex-1" disabled={loading} data-testid="investment-submit-button">
              {loading ? 'Wird erstellt...' : 'Erstellen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditInvestmentModal = ({ investment, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    product_code: investment.product_code,
    principal_eur: investment.principal_eur,
    payout_mode: investment.payout_mode,
    payout_timing: investment.payout_timing,
    interest_start_date: investment.interest_start_date ? investment.interest_start_date.split('T')[0] : '',
    duration_months: investment.duration_months || 36,
    notes: investment.notes || '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.patch(`${API}/admin/investments/${investment.id}`, formData);
      toast.success('Investment aktualisiert - Neuberechnung läuft automatisch');
      onSuccess();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-heading font-bold mb-4">Investment bearbeiten</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Produkt</label>
            <select
              value={formData.product_code}
              onChange={(e) => setFormData({ ...formData, product_code: e.target.value })}
              className="input-field"
              data-testid="edit-investment-product-select"
            >
              <option value="BASIS">BASIS (5.0% p.a.)</option>
              <option value="ERHÖHT">ERHÖHT (6.5% p.a.)</option>
              <option value="PREMIUM">PREMIUM (9.7% p.a.)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Einlage (EUR)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              className="input-field"
              value={formData.principal_eur}
              onChange={(e) => setFormData({ ...formData, principal_eur: parseFloat(e.target.value) })}
              required
              data-testid="edit-investment-principal-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Zinsbeginn (Startdatum)</label>
            <input
              type="date"
              className="input-field"
              value={formData.interest_start_date}
              onChange={(e) => setFormData({ ...formData, interest_start_date: e.target.value })}
              required
              data-testid="edit-investment-start-date-input"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Änderungen triggern automatische Neuberechnung aller Zinsen und Auszahlungen
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Laufzeit (Monate)</label>
            <div className="flex gap-2 items-center">
              <input
                type="number"
                min={1}
                max={240}
                value={formData.duration_months}
                onChange={(e) => setFormData({ ...formData, duration_months: parseInt(e.target.value) || 0 })}
                className="input-field flex-1"
                data-testid="edit-investment-duration-input"
              />
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                {formData.duration_months > 0 && (
                  <>≈ {(formData.duration_months / 12).toFixed(formData.duration_months % 12 === 0 ? 0 : 1)} Jahre</>
                )}
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {[12, 18, 24, 36, 48, 60].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setFormData({ ...formData, duration_months: m })}
                  className={`text-xs px-2.5 py-1 rounded-full border transition ${
                    formData.duration_months === m
                      ? 'bg-accent text-white border-accent'
                      : 'border-gray-300 hover:border-accent hover:bg-accent/5'
                  }`}
                  data-testid={`edit-duration-quick-${m}`}
                >
                  {m}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Beliebige Laufzeit zwischen 1 und 240 Monaten. Nach Ablauf stoppen die Zinsen automatisch.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Auszahlungsmodus</label>
            <select
              value={formData.payout_mode}
              onChange={(e) => setFormData({ ...formData, payout_mode: e.target.value })}
              className="input-field"
              data-testid="edit-investment-payout-mode-select"
            >
              <option value="MONTHLY">Monatlich</option>
              <option value="YEARLY">Jährlich</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Auszahlungszeitpunkt</label>
            <select
              value={formData.payout_timing}
              onChange={(e) => setFormData({ ...formData, payout_timing: e.target.value })}
              className="input-field"
              data-testid="edit-investment-payout-timing-select"
            >
              {formData.payout_mode === 'MONTHLY' ? (
                <>
                  <option value="MONTH_START">Monatsanfang (1. des Monats)</option>
                  <option value="MONTH_END">Monatsende</option>
                  <option value="ANNIVERSARY_MONTHLY">Monatlicher Jahrestag</option>
                </>
              ) : (
                <>
                  <option value="ANNIVERSARY_YEARLY">Jährlicher Jahrestag</option>
                  <option value="YEAR_END">Jahresende (31.12.)</option>
                </>
              )}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Notizen</label>
            <textarea
              className="input-field"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              placeholder="Interne Notizen zum Investment..."
              data-testid="edit-investment-notes-input"
            />
          </div>

          <div className="flex space-x-3 mt-6">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Abbrechen</button>
            <button type="submit" className="btn-primary flex-1" disabled={loading} data-testid="edit-investment-submit-button">
              {loading ? 'Wird gespeichert...' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditBankModal = ({ customer, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    bank_account_holder: customer.bank_account_holder || '',
    bank_iban: customer.bank_iban || '',
    bank_bic: customer.bank_bic || '',
    bank_name: customer.bank_name || '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.patch(`${API}/admin/customers/${customer.id}/bank`, formData);
      toast.success('Bankverbindung aktualisiert');
      onSuccess();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    } finally {
      setLoading(false);
    }
  };

  const formatIBAN = (value) => {
    return value.toUpperCase().replace(/\s/g, '');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-heading font-bold mb-4">Bankverbindung bearbeiten</h3>
        <p className="text-sm text-muted-foreground mb-6">Kunde: {customer.name || customer.email}</p>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Kontoinhaber</label>
            <input
              type="text"
              className="input-field"
              value={formData.bank_account_holder}
              onChange={(e) => setFormData({ ...formData, bank_account_holder: e.target.value })}
              placeholder="Max Mustermann"
              data-testid="edit-bank-holder-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">IBAN</label>
            <input
              type="text"
              className="input-field font-mono"
              value={formData.bank_iban}
              onChange={(e) => setFormData({ ...formData, bank_iban: formatIBAN(e.target.value) })}
              placeholder="DE89370400440532013000"
              maxLength={34}
              data-testid="edit-bank-iban-input"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {formData.bank_iban && formData.bank_iban.replace(/(.{4})/g, '$1 ').trim()}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">BIC</label>
              <input
                type="text"
                className="input-field font-mono"
                value={formData.bank_bic}
                onChange={(e) => setFormData({ ...formData, bank_bic: e.target.value.toUpperCase() })}
                placeholder="COBADEFFXXX"
                maxLength={11}
                data-testid="edit-bank-bic-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Bank</label>
              <input
                type="text"
                className="input-field"
                value={formData.bank_name}
                onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                placeholder="Commerzbank AG"
                data-testid="edit-bank-name-input"
              />
            </div>
          </div>

          <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 mt-4">
            <div className="flex items-start space-x-2">
              <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-sm text-amber-800">
                Änderungen werden im Audit-Log protokolliert.
              </p>
            </div>
          </div>

          <div className="flex space-x-3 mt-6">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Abbrechen</button>
            <button type="submit" className="btn-primary flex-1" disabled={loading} data-testid="edit-bank-submit">
              {loading ? 'Wird gespeichert...' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AdminDocumentsTab = ({ customerId }) => {
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showUploadMenu, setShowUploadMenu] = useState(false);

  useEffect(() => { loadDocs(); }, []);

  const loadDocs = async () => {
    try {
      const res = await axios.get(`${API}/admin/customers/${customerId}/documents`);
      setDocuments(res.data);
    } catch (e) { toast.error('Fehler beim Laden der Dokumente'); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/admin/customers/${customerId}/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Dokument hochgeladen');
      loadDocs();
    } catch (err) { toast.error('Fehler beim Hochladen'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleCameraScan = async () => {
    setShowUploadMenu(false);
    try {
      const file = await scanDocumentFromCamera('camera');
      if (!file) return;
      
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/admin/customers/${customerId}/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Dokument gescannt und hochgeladen');
      loadDocs();
    } catch (err) { toast.error('Fehler beim Scannen'); }
    finally { setUploading(false); }
  };

  return (
    <div className="card" data-testid="admin-documents-tab">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-semibold">Dokumente ({documents.length})</h3>
        <div className="relative">
          <button 
            onClick={() => setShowUploadMenu(!showUploadMenu)}
            className="btn-primary text-sm flex items-center gap-2"
            disabled={uploading}
            data-testid="admin-upload-doc-button"
          >
            {uploading ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Wird hochgeladen...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Hochladen
              </>
            )}
          </button>
          {showUploadMenu && !uploading && (
            <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50" style={{ animation: 'fadeInUp 0.2s ease-out' }}>
              <label className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors">
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="text-sm text-gray-700">Datei auswählen</span>
                <input type="file" className="hidden" onChange={(e) => { setShowUploadMenu(false); handleUpload(e); }} accept=".pdf,.jpg,.jpeg,.png" />
              </label>
              <button 
                onClick={handleCameraScan}
                className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 w-full text-left transition-colors"
              >
                <svg className="w-5 h-5 text-[#C5A059]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-sm text-gray-700">Dokument scannen</span>
              </button>
            </div>
          )}
        </div>
      </div>
      {documents.length === 0 ? (
        <p className="text-muted-foreground text-center py-8">Keine Dokumente</p>
      ) : (
        <div className="space-y-2">
          {documents.map(doc => (
            <div key={doc.id} className="flex justify-between items-center p-3 border rounded-lg" data-testid={`admin-doc-${doc.id}`}>
              <div>
                <p className="font-medium text-sm">{doc.title}</p>
                <p className="text-xs text-muted-foreground">
                  {doc.doc_type} | {format(new Date(doc.created_at), 'dd.MM.yyyy', { locale: de })}
                </p>
              </div>
              <a href={`${API}/documents/${doc.id}/download`} className="text-accent hover:underline text-sm">
                Download
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AdminMessagesTab = ({ customerId, customerName }) => {
  const [messages, setMessages] = useState([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [attachmentOriginalSize, setAttachmentOriginalSize] = useState(0);
  const [compressing, setCompressing] = useState(false);
  const [sending, setSending] = useState(false);
  const fileInputRef = React.useRef(null);

  useEffect(() => { loadMessages(); }, []);

  const loadMessages = async () => {
    try {
      const res = await axios.get(`${API}/admin/customers/${customerId}/messages`);
      setMessages(res.data);
    } catch (e) { console.error(e); }
  };

  const acceptFile = async (raw) => {
    if (!raw) return;
    const allowed = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!allowed.includes(raw.type)) { toast.error('Nur PDF, JPG und PNG erlaubt'); return; }
    const originalSize = raw.size;
    setCompressing(true);
    try {
      const compressed = await compressAttachment(raw);
      if (compressed.size > 10 * 1024 * 1024) {
        toast.error('Datei ist auch nach Komprimierung über 10 MB');
        return;
      }
      setAttachment(compressed);
      setAttachmentOriginalSize(originalSize);
      if (compressed.size < originalSize * 0.95) {
        const orig = (originalSize / 1024 / 1024).toFixed(1);
        const comp = (compressed.size / 1024 / 1024).toFixed(1);
        toast.success(`Komprimiert: ${orig} MB → ${comp} MB`);
      }
    } catch (e) {
      console.error(e);
      toast.error('Komprimierung fehlgeschlagen');
    } finally {
      setCompressing(false);
    }
  };

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    acceptFile(f);
  };

  const scanAttachment = async () => {
    try {
      const f = await scanDocumentFromCamera('camera');
      if (!f) return;
      await acceptFile(f);
    } catch (e) {
      toast.error('Fehler beim Scannen');
    }
  };

  const sendMessage = async () => {
    if (!subject.trim() || !body.trim()) { toast.error('Betreff und Nachricht erforderlich'); return; }
    setSending(true);
    try {
      const recipientId = parseInt(customerId);
      if (isNaN(recipientId)) {
        toast.error('Ungültige Kunden-ID');
        return;
      }
      const form = new FormData();
      form.append('recipient_id', String(recipientId));
      form.append('subject', subject);
      form.append('body', body);
      if (attachment) form.append('file', attachment);
      await axios.post(`${API}/messages`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Nachricht gesendet');
      setSubject('');
      setBody('');
      setAttachment(null);
      setAttachmentOriginalSize(0);
      loadMessages();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Unbekannter Fehler';
      console.error('Message send error:', err.response?.status, detail);
      toast.error(`Fehler beim Senden: ${detail}`);
    }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-messages-tab">
      <div className="card">
        <h3 className="font-semibold mb-3">Nachricht an {customerName} senden</h3>
        <div className="space-y-3">
          <input type="text" className="input-field" placeholder="Betreff..." value={subject}
            onChange={e => setSubject(e.target.value)} data-testid="admin-msg-subject" />
          <textarea className="input-field" rows={3} placeholder="Nachricht..." value={body}
            onChange={e => setBody(e.target.value)} data-testid="admin-msg-body" />

          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileInputRef} type="file" className="hidden"
              accept=".pdf,.jpg,.jpeg,.png" onChange={pickFile}
              data-testid="admin-msg-file-input"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={compressing}
              className="text-sm flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition disabled:opacity-50"
              data-testid="admin-msg-attach-button"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
              Datei anhängen
            </button>
            <button
              type="button"
              onClick={scanAttachment}
              disabled={compressing}
              className="text-sm flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition disabled:opacity-50"
              data-testid="admin-msg-scan-button"
            >
              <svg className="w-4 h-4" style={{ color: '#C5A059' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Scannen
            </button>
            {compressing && (
              <span className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="admin-msg-compressing">
                <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
                Wird komprimiert...
              </span>
            )}
            {attachment && !compressing && (
              <div
                className="flex items-center gap-2 text-xs px-2 py-1 rounded-md bg-accent/10 text-primary"
                data-testid="admin-msg-attachment-preview"
              >
                <span className="truncate" style={{ maxWidth: '180px' }}>{attachment.name}</span>
                <span className="text-muted-foreground">
                  {(attachment.size / 1024 / 1024).toFixed(2)} MB
                  {attachmentOriginalSize > attachment.size * 1.05 && (
                    <> · <span className="line-through">{(attachmentOriginalSize / 1024 / 1024).toFixed(1)} MB</span></>
                  )}
                </span>
                <button
                  type="button"
                  onClick={() => { setAttachment(null); setAttachmentOriginalSize(0); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                  className="text-gray-500 hover:text-red-600"
                  aria-label="Anhang entfernen"
                  data-testid="admin-msg-remove-attachment"
                >
                  ✕
                </button>
              </div>
            )}
          </div>

          <button onClick={sendMessage} disabled={sending || compressing} className="btn-primary text-sm" data-testid="admin-send-msg">
            {sending ? 'Wird gesendet...' : 'Senden'}
          </button>
        </div>
      </div>

      <div className="card">
        <h3 className="font-semibold mb-3">Nachrichtenverlauf ({messages.length})</h3>
        {messages.length === 0 ? (
          <p className="text-muted-foreground text-center py-6">Keine Nachrichten</p>
        ) : (
          <div className="space-y-3">
            {messages.map(msg => (
              <div key={msg.id} className={`p-3 rounded-lg ${msg.sender_role === 'ADMIN' ? 'bg-blue-50 ml-8' : 'bg-gray-50 mr-8'}`}
                data-testid={`admin-msg-${msg.id}`}>
                <div className="flex justify-between items-start mb-1">
                  <span className="text-xs font-medium">{msg.sender_name}</span>
                  <span className="text-xs text-muted-foreground">
                    {format(new Date(msg.created_at), 'dd.MM.yyyy HH:mm', { locale: de })}
                  </span>
                </div>
                <p className="text-sm font-medium">{msg.subject}</p>
                <FormattedMessage text={msg.body} className="text-sm text-muted-foreground mt-1" />
                <MessageAttachment message={msg} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const AuditLogTab = ({ customerId, auditLogs, loading, onLoad }) => {
  useEffect(() => {
    if (auditLogs.length === 0) onLoad();
  }, []);

  const translateAction = (action) => {
    const map = {
      'CREATE_INVESTMENT': 'Investment erstellt',
      'UPDATE_INVESTMENT': 'Investment aktualisiert',
      'UPDATE_STATUS': 'Status geändert',
      'RECALCULATE': 'Neuberechnung',
      'BULK_CONFIRM_PAYOUTS': 'Bulk-Bestätigung',
      'UPDATE_BANK': 'Bankdaten geändert',
      'ACTIVATE_USER': 'Konto aktiviert',
      'REJECT_USER': 'Konto abgelehnt',
    };
    return map[action] || action;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="card" data-testid="audit-log-tab">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-semibold">Audit Log ({auditLogs.length})</h3>
        <button onClick={onLoad} className="text-sm text-accent hover:underline">Aktualisieren</button>
      </div>
      {auditLogs.length === 0 ? (
        <p className="text-muted-foreground text-center py-8">Keine Audit-Einträge</p>
      ) : (
        <div className="space-y-3">
          {auditLogs.map((log) => (
            <div key={log.id} className="p-3 border rounded-lg" data-testid={`audit-log-${log.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <span className="text-sm font-medium">{translateAction(log.action)}</span>
                  <span className="text-xs text-muted-foreground ml-2">({log.entity_type} #{log.entity_id})</span>
                </div>
                <span className="text-xs text-muted-foreground">
                  {format(new Date(log.created_at), 'dd.MM.yyyy HH:mm', { locale: de })}
                </span>
              </div>
              {log.before_json && (
                <div className="mt-2 text-xs">
                  <span className="text-muted-foreground">Vorher: </span>
                  <code className="bg-red-50 text-red-800 px-1 rounded">{log.before_json}</code>
                </div>
              )}
              {log.after_json && (
                <div className="mt-1 text-xs">
                  <span className="text-muted-foreground">Nachher: </span>
                  <code className="bg-green-50 text-green-800 px-1 rounded">{log.after_json}</code>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminCustomerDetail;

// Modal for Vertragsverlängerung
const ExtendInvestmentModal = ({ investment, onClose, onSuccess }) => {
  const [additionalMonths, setAdditionalMonths] = useState(12);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (additionalMonths < 1) { toast.error('Mindestens 1 Monat'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/investments/${investment.id}/extend`, {
        additional_months: parseInt(additionalMonths, 10),
        note: note.trim() || null,
      });
      toast.success(`Vertrag um ${additionalMonths} Monate verlängert`);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verlängerung fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const currentDuration = investment.duration_months || 0;
  const newDuration = currentDuration + parseInt(additionalMonths || 0, 10);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="extend-investment-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-heading font-bold mb-4">Vertrag verlängern</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Aktuelle Laufzeit: <strong>{currentDuration} Monate</strong>
        </p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Zusätzliche Monate</label>
            <input
              type="number"
              min={1}
              max={240}
              className="input-field"
              value={additionalMonths}
              onChange={(e) => setAdditionalMonths(e.target.value)}
              required
              data-testid="extend-additional-months-input"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Neue Gesamtlaufzeit: <strong>{newDuration} Monate</strong>
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Notiz (optional)</label>
            <textarea
              className="input-field" rows={2}
              value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="z.B. Kunde wünscht Verlängerung"
              data-testid="extend-note-input"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Abbrechen</button>
            <button
              type="submit" className="btn-primary flex-1" disabled={saving}
              data-testid="extend-submit-button"
            >
              {saving ? 'Wird verlängert...' : 'Verlängern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
